import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../config/axiosInstance';
import PageTitle from '../../components/PageTitle';
import AllProjectsTable from '../../components/project/AllProjectsTable';

export default function AllProjects(){
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [brands, setBrands] = useState([]);
    const [designers, setDesigners] = useState([]);
    const [summary, setSummary] = useState({ total: 0, pending: 0, inProgress: 0, completed: 0 });

    const [status, setStatus] = useState('');
    const [brand, setBrand] = useState('');
    const [designer, setDesigner] = useState('');
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [hasMorePages, setHasMorePages] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        fetchProjects(currentPage, debouncedQuery);
    }, [status, brand, designer, currentPage, debouncedQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [status, brand, designer, debouncedQuery]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timeout);
    }, [query]);

    const fetchInitialData = async () => {
        try{
            const [brandsResponse, designersResponse] = await Promise.all([
                axiosInstance.get('clients/brands'),
                axiosInstance.get('admin/users')
            ]);
            setBrands(brandsResponse.data.data.brands || []);
            setDesigners(designersResponse.data.data || []);
        } catch(error){
            console.log(error);
        }
    }

    const fetchProjects = async (page = 1, search = '') => {
        setIsLoading(true);
        try{
            const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
            if(status) params.set('status', status);
            if(brand) params.set('brand', brand);
            if(designer) params.set('designer', designer);
            if(search) params.set('search', search);
            const response = await axiosInstance.get(`clients/projects/filter?${params.toString()}`);
            const payload = response.data?.data || {};
            const totalCount = payload.total ?? payload.totalCount ?? payload.total_count ?? payload.meta?.total ?? 0;
            const records = Array.isArray(payload.projects) ? payload.projects : Array.isArray(payload) ? payload : payload.items || [];
            setProjects(records);

            const counts = payload.summary || payload.counts || payload.metrics || {};
            if(Object.keys(counts).length > 0){
                setSummary({
                    total: counts.total ?? totalCount ?? records.length,
                    pending: counts.pending ?? counts.status0 ?? records.filter(p => p.status === 0).length,
                    inProgress: counts.inProgress ?? counts.status1 ?? records.filter(p => p.status === 1).length,
                    completed: counts.completed ?? counts.status3 ?? records.filter(p => p.status === 3).length
                });
            } else {
                setSummary({
                    total: totalCount || records.length,
                    pending: records.filter(p => p.status === 0).length,
                    inProgress: records.filter(p => p.status === 1).length,
                    completed: records.filter(p => p.status === 3).length
                });
            }

            const hasMore = typeof payload.hasMore === 'boolean'
                ? payload.hasMore
                : totalCount ? (page * pageSize) < totalCount : records.length === pageSize;
            setHasMorePages(hasMore);

        }catch(error){
            console.log(error);
        }finally{
            setIsLoading(false);
        }
    }

    const handleClearFilter = () => {
        setStatus('');
        setBrand('');
        setDesigner('');
        setQuery('');
        setDebouncedQuery('');
        setCurrentPage(1);
    }

    const filteredProjects = useMemo(() => {
        if (!Array.isArray(projects)) return [];
        // Sort by updated date (fallback to createdAt) descending
        return [...projects].sort((a, b) => {
            const aDate = new Date(a.updatedAt || a.updated_at || a.createdAt || 0).getTime();
            const bDate = new Date(b.updatedAt || b.updated_at || b.createdAt || 0).getTime();
            return bDate - aDate;
        });
    }, [projects]);

    const statusOptions = useMemo(() => ([
        { value: '', label: 'All status' },
        { value: '0', label: 'Pending' },
        { value: '1', label: 'In progress' },
        { value: '2', label: 'Design ready' },
        { value: '3', label: 'Completed' },
        { value: '7', label: 'Cancelled' }
    ]), []);

    const brandOptions = useMemo(() => ([
        { value: '', label: 'All brands' },
        ...brands.map(b => ({ value: b.id, label: b.brand_name }))
    ]), [brands]);

    const designerOptions = useMemo(() => ([
        { value: '', label: 'All designers' },
        ...designers.map(d => ({ value: d.id, label: `${d.firstname} ${d.lastname}`.trim() }))
    ]), [designers]);

    return(
        <>
            <PageTitle title="Projects" />

            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">Projects</h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">Track every creative request, status, and delivery.</p>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                        <Link to="/manage/projects/create" className="inline-flex items-center gap-2 rounded-xl bg-gray-900 dark:bg-gray-700 px-4 py-2 text-white shadow-md hover:bg-gray-700 dark:hover:bg-gray-600">New Request</Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Total Projects" value={summary.total} bg="bg-gray-100" text="text-gray-800" />
                    <StatCard label="Pending" value={summary.pending} bg="bg-amber-100" text="text-amber-600" />
                    <StatCard label="In Progress" value={summary.inProgress} bg="bg-blue-100" text="text-blue-600" />
                    <StatCard label="Completed" value={summary.completed} bg="bg-emerald-100" text="text-emerald-600" />
                </div>

                <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div className="flex flex-wrap gap-3">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Search</label>
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search projects..."
                                    className="h-10 w-64 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-600"
                                />
                            </div>
                            <FilterSelect label="Status" value={status} onChange={setStatus} options={statusOptions} />
                            <FilterSelect label="Brand" value={brand} onChange={setBrand} options={brandOptions} />
                            <FilterSelect label="Designer" value={designer} onChange={setDesigner} options={designerOptions} />
                        </div>
                        <button onClick={handleClearFilter} className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">Clear filters</button>
                    </div>
                </div>

                <AllProjectsTable projects={filteredProjects} isLoading={isLoading} enableFilters={false} enableDensityToggle={false} />

                <Pagination currentPage={currentPage} onChange={setCurrentPage} disabled={isLoading} hasMore={hasMorePages} />
            </div>
        </>
    )
}

function StatCard({ label, value, bg, text }) {
    return (
        <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <span className={`${bg} dark:bg-gray-700 ${text} dark:text-gray-100 mt-2 inline-flex h-12 w-12 items-center justify-center rounded-xl text-2xl font-bold`}>{value}</span>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options }) {
    return (
        <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-600"
            >
                {options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );
}

function Pagination({ currentPage, onChange, disabled, hasMore }) {
    return (
        <div className="flex items-center justify-between rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-400 shadow-sm">
            <button
                onClick={() => onChange(Math.max(1, currentPage - 1))}
                disabled={disabled || currentPage === 1}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            >
                Prev
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Page {currentPage}</span>
            <button
                onClick={() => onChange(currentPage + 1)}
                disabled={disabled || !hasMore}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            >
                Next
            </button>
        </div>
    );
}